import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './config';
import AnalyticsService from './analytics';

const SENSITIVE_KEYS = new Set([
  'email',
  'password',
  'passwd',
  'token',
  'idToken',
  'refreshToken',
  'authorization',
]);

class ErrorTrackingService {
  constructor() {
    this.errorCollection = collection(db, 'errors');
    this.setupGlobalErrorHandlers();
  }

  /**
   * Set up global error handlers
   */
  setupGlobalErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(event.reason, 'unhandled_rejection');
    });

    // Handle uncaught exceptions
    window.addEventListener('error', (event) => {
      this.logError(event.error, 'uncaught_exception');
    });
  }

  sanitizeAdditionalData(additionalData = {}) {
    const clean = {};
    Object.entries(additionalData || {}).forEach(([key, value]) => {
      if (SENSITIVE_KEYS.has(key) || /password|token|secret|email/i.test(key)) {
        return;
      }
      if (value == null) {
        return;
      }
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        clean[key] = typeof value === 'string' ? value.slice(0, 1000) : value;
      }
    });
    return clean;
  }

  /**
   * Log an error to Firestore and Analytics
   * @param {Error} error - The error object
   * @param {string} context - Where the error occurred
   * @param {Object} [additionalData] - Any additional data to log
   */
  async logError(error, context, additionalData = {}) {
    const err = error instanceof Error ? error : new Error(String(error || 'Unknown error'));

    try {
      AnalyticsService.logError(err, context);
    } catch (_) {
      // ignore analytics failures
    }

    // Skip logging the logger's own permission failures (would loop).
    if (context === 'error_log') {
      console.warn('Error log failed:', err.message);
      return;
    }

    const uid = getAuth()?.currentUser?.uid;
    const allowedExtra = this.sanitizeAdditionalData(additionalData);
    const errorData = {
      message: String(err.message || 'Unknown error').slice(0, 2000),
      context: String(context || 'unknown').slice(0, 200),
      timestamp: serverTimestamp(),
      userAgent: String(navigator.userAgent || '').slice(0, 500),
      url: String(window.location.href || '').slice(0, 500),
      acknowledged: false,
    };
    if (uid) {
      errorData.userId = uid;
    }
    if (err.stack) {
      errorData.stack = String(err.stack).slice(0, 2000);
    }
    if (err.code) {
      errorData.errorCode = String(err.code).slice(0, 200);
    }
    ['code', 'operation', 'endpoint', 'status', 'statusText', 'details'].forEach((key) => {
      if (allowedExtra[key] !== undefined) {
        errorData[key] = allowedExtra[key];
      }
    });

    try {
      await addDoc(this.errorCollection, errorData);
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  }

  /**
   * Log a Firebase-specific error
   * @param {Error} error - The Firebase error
   * @param {string} operation - The operation that failed
   */
  async logFirebaseError(error, operation) {
    await this.logError(error, 'firebase_operation', {
      operation,
      code: error.code,
      details: error.details,
    });
  }

  /**
   * Log an API error
   * @param {Error} error - The API error
   * @param {string} endpoint - The API endpoint
   * @param {Object} [requestData] - The request data
   */
  async logApiError(error, endpoint, requestData = {}) {
    await this.logError(error, 'api_call', {
      endpoint,
      requestData,
      status: error.status,
      statusText: error.statusText,
    });
  }
}

export const errorTracking = new ErrorTrackingService();
export default errorTracking; 